<!--
  El disparador ES el campo de búsqueda.

  La versión anterior era un botón con un `<input>` de filtro dentro del panel:
  el usuario abría, bajaba la vista al panel y escribía en un segundo control
  que no era el que había pulsado. Ahora se escribe donde se hizo clic y la
  lista se filtra debajo, que es lo que hace cualquier autocompletado.
-->
<div class="dxc-combo__field" [class.is-invalid]="invalid" [class.is-disabled]="disabled">
  <input #field type="text" class="dxc-combo__input" role="combobox" autocomplete="off"
    spellcheck="false" aria-autocomplete="list"
    [id]="selectId" [disabled]="disabled"
    [attr.aria-expanded]="open" [attr.aria-controls]="open ? panelId : null"
    [attr.aria-activedescendant]="open && activeIndex >= 0 ? panelId + '-opt-' + activeIndex : null"
    [attr.aria-label]="ariaLabel || null"
    [value]="displayText" [placeholder]="inputPlaceholder"
    (input)="onType($any($event.target).value)"
    (focus)="onFocus()"
    (mousedown)="onFieldDown($event)"
    (keydown)="onKeydown($event)">

  @if (value && !disabled && clearable) {
    <button type="button" class="dxc-combo__clear" tabindex="-1" aria-label="Limpiar selección"
      (mousedown)="$event.preventDefault()" (click)="clear()">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="m6.5 6.5 11 11M17.5 6.5l-11 11"/></svg>
    </button>
  }

  <!-- tabindex -1: el tabulador ya se detiene en el input; un segundo alto aquí
       obligaría a pulsar Tab dos veces para salir del campo. -->
  <button type="button" class="dxc-combo__toggle" tabindex="-1" [disabled]="disabled"
    [attr.aria-label]="open ? 'Cerrar lista' : 'Abrir lista'"
    (mousedown)="$event.preventDefault()" (click)="toggle()">
    <svg class="dxc-combo__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
  </button>
</div>

@if (open) {
  <!--
    `position: fixed` con coordenadas calculadas del campo. Es lo que impide que
    un ancestro con `overflow: hidden` —la tarjeta de contenedor, el propio
    `.dxc-input-group`— recorte el panel. Con `absolute` el panel se quedaba
    dentro de la caja recortada y solo se veían las dos primeras opciones.
  -->
  <ul class="dxc-combo__panel" role="listbox" [id]="panelId"
    [class.dxc-combo__panel--up]="panelUp"
    [class.dxc-combo__panel--auto]="panelWidth === 'auto'"
    [style]="panelStyle">
    @let view = optionView;
    <!-- `track $index` y no `track option.value`: un catálogo del backend puede
         traer claves repetidas —las ubicaciones guardadas traían `null` dos
         veces y tres nombres duplicados— y eso es un NG0955. Además la lista se
         recorta y se filtra entera, así que la posición ES la identidad. -->
    @for (option of view.list; track $index; let i = $index) {
      @if (option.group && option.group !== view.list[i - 1]?.group) {
        <li class="dxc-combo__group">{{ option.group }}</li>
      }
      <li>
        <!-- mousedown.preventDefault: que el clic no robe el foco del input, o el
             panel se cerraría antes de registrar la selección. -->
        <button type="button" class="dxc-combo__option" role="option"
          [id]="panelId + '-opt-' + i" [disabled]="option.disabled"
          [attr.aria-selected]="option.value === value"
          [class.dxc-combo__option--active]="i === activeIndex"
          (mousedown)="$event.preventDefault()" (mouseenter)="activeIndex = i" (click)="select(option)">
          @if (value) {
            <span class="dxc-combo__check">
              @if (option.value === value) {
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4 12 6 6L20 6"/></svg>
              }
            </span>
          }
          @if (option.iconUrl) { <img class="dxc-combo__flag" [src]="option.iconUrl" alt="" aria-hidden="true"> }
          @if (option.icon) { <span [class]="option.icon" aria-hidden="true"></span> }
          <!-- El resaltado solo cuando hay algo que resaltar: `highlight()`
               devuelve un `SafeHtml` nuevo en cada llamada, así que el
               `[innerHTML]` se reescribía en cada ciclo para TODAS las opciones
               aunque nadie estuviera escribiendo. -->
          @if (typing && query.trim()) {
            <span class="dxc-combo__option-label" [innerHTML]="highlight(option.label)"></span>
          } @else {
            <span class="dxc-combo__option-label">{{ optionLabel(option) }}</span>
          }
          @if (option.meta) { <span class="dxc-combo__option-meta">{{ option.meta }}</span> }
        </button>
      </li>
    }
    @if (!view.list.length) {
      <li class="dxc-combo__empty">{{ query.trim() ? emptyLabel + ' para «' + query.trim() + '»' : emptyLabel }}</li>
    }
    <!-- Nada se recorta en silencio: si quedan fuera, se dice cuántas y qué
         hacer para verlas. Reutiliza el pie que ya usa la multiselección. -->
    @if (view.hidden) {
      <li class="dxc-combo__foot">
        <span>{{ view.hidden }} {{ view.hidden === 1 ? 'opción más' : 'opciones más' }}</span>
        <span>Escribe para afinar la búsqueda</span>
      </li>
    }
  </ul>
}
